import { PublicShell } from "@/components/PublicShell";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Users, Store, HeartHandshake, LifeBuoy } from "lucide-react";

const groups = [
  {
    id: "beneficiaries", title: "Beneficiaries", icon: Users,
    faqs: [
      ["How do I register?", "Register online from the homepage, via a TMF Enrollment Agent, or through USSD *7006*99#. You only need your phone number and a few personal details to create your permanent TMF profile."],
      ["How do I get my TMF ID?", "As soon as your registration is verified, the system generates a unique, permanent TMF Beneficiary ID (e.g. TMF-BNF-0000000123). This ID stays with you for every programme you ever join."],
      ["How do I receive my voucher?", "When a sponsor allocates a benefit to your number, you receive an SMS with a TMF Claim Code. The digital voucher also appears in your beneficiary portal and via USSD."],
      ["How do I check my balance?", "Dial *7006*99# on the registered SIM, log in to your beneficiary portal, or use 'Check My Benefit' on the website with your phone number."],
      ["Where can I redeem?", "At any verified TMF Vendor. Use 'Find a Vendor' to locate the nearest one, or ask any TMF-branded shop."],
      ["Can I redeem in another state?", "Yes. Your entitlement is not tied to a location. Registered in Lagos but travelling to Kano? You can redeem with any verified TMF Vendor nationwide."],
      ["What happens to my unused balance?", "Nothing is lost. Partial redemption means unused value stays on your voucher until it is fully redeemed or the programme expires."],
      ["What if my phone is lost?", "Your TMF ID and balance live on the central TMF ledger, not on your phone. Contact support or an Enrollment Agent to re-verify and regain access."],
      ["What if my voucher doesn't work?", "Ask the vendor to re-verify the claim code. If it still fails, contact the TMF Help Centre with your TMF ID — your balance is safe on the ledger."],
    ],
  },
  {
    id: "vendors", title: "Vendors", icon: Store,
    faqs: [
      ["How do I register?", "Create a vendor account from 'Sponsors & Vendors'. Provide your business details, location and settlement bank account. Once verified you appear on the TMF Vendor map."],
      ["How do I verify a beneficiary?", "In your vendor terminal, enter the beneficiary's TMF Claim Code. The system confirms the voucher and available balance before you redeem."],
      ["How do I receive settlement?", "Approved redemptions are grouped into settlement batches by TMF Finance and paid to the bank account on your vendor profile. You can track pending and settled amounts in your portal."],
      ["What happens if there is a disputed transaction?", "Raise it through the Complaints & Dispute Resolution Centre. A ticket number is generated and TMF can review and, where justified, reverse the transaction on the ledger."],
    ],
  },
  {
    id: "sponsors", title: "Sponsors", icon: HeartHandshake,
    faqs: [
      ["How do I launch a programme?", "Create a sponsor account, define your programme (benefit type, amount per beneficiary, target and eligible states), fund it, then allocate benefits to beneficiary phone numbers — individually or via bulk CSV upload."],
      ["How are beneficiaries verified?", "Every beneficiary holds a unique, de-duplicated TMF ID. One person maps to one core profile, preventing double enrolment across programmes."],
      ["How are funds tracked?", "Your dashboard shows funding, activation, redemption, unredeemed balance, redemption rate, states covered and participating vendors in real time — every naira is traceable."],
      ["How do I receive reports?", "Download an Impact Report (PDF/CSV) any time from your sponsor dashboard, summarising reach, redemption and economic activity across your programmes."],
    ],
  },
];

export default function HelpCentre() {
  return (
    <PublicShell>
      <section className="bg-emerald-950 tmf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <div className="flex items-center gap-3"><LifeBuoy className="w-7 h-7 text-emerald-300" /><span className="text-xs font-mono uppercase tracking-widest text-emerald-300">TMF Help Centre</span></div>
          <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight max-w-3xl">Answers for beneficiaries, vendors and sponsors.</h1>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 space-y-12">
        {groups.map((g) => (
          <div key={g.id} data-testid={`help-group-${g.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><g.icon className="w-5 h-5 text-emerald-600" /></div>
              <h2 className="font-display font-bold text-2xl text-emerald-950">{g.title}</h2>
            </div>
            <Accordion type="single" collapsible className="mt-5">
              {g.faqs.map(([q, a], i) => (
                <AccordionItem key={q} value={`${g.id}-${i}`} data-testid={`help-faq-${g.id}-${i}`} className="border-emerald-100">
                  <AccordionTrigger className="text-left text-emerald-950 hover:text-emerald-700 text-sm font-medium">{q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm leading-relaxed">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </section>
    </PublicShell>
  );
}
